import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';

import { AppMessage } from '@/common/components';
import { FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { DomainRegluingsFormFields, DomainRegluingsFormInputs, DomainVisitors } from '@/modules/Domains/domains.interfaces';
import { DomainRegluingsQueryKeys, SiteDomainsQueryKeys, useCreateDomainRegluingsMutation } from '@/modules/Domains/queries';

import { useAppPagination } from '@/common/hooks';
import { DomainRegluingsCreateFormView } from './DomainRegluingsFormView';
import { domainRegluingsFormValidationSchema } from './validation';

interface Props {
  siteId: string;
  domainId: string;
  handleToggleRegluingFormVisibility: () => void;
}

export const DomainRegluingsCreateFormContainer: FC<Props> = memo(
  ({ domainId, siteId, handleToggleRegluingFormVisibility }) => {
    const queryClient = useQueryClient();
    const appMessageApi = AppMessage.useControls();
    const { pageParam: currentPage } = useAppPagination();

    const [form] = Form.useForm<Pick<DomainRegluingsFormFields, 'forVisitor' | 'isAutoUpdate' | 'targetDomainId'>>();
    const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof DomainRegluingsFormInputs>>(null);

    const { mutate: createDomainRegluingMutation, isLoading: createDomainRegluingIsLoading } =
      useCreateDomainRegluingsMutation(
        siteId,
        domainId
      )({
        onSuccess: async () => {
          await queryClient.refetchQueries([DomainRegluingsQueryKeys.GetAllRegluings, siteId, domainId]);
          await queryClient.refetchQueries([SiteDomainsQueryKeys.GetAllDomains, siteId, { page: currentPage }]);

          appMessageApi.success('Переклейку успішно створено!');

          handleBack();
        },
        onError: (err) => {
          setFormErrors(extractFormErrors<typeof DomainRegluingsFormInputs>(err));
          messageOnError(err, appMessageApi);
        }
      });

    const initialValues: Pick<DomainRegluingsFormFields, 'forVisitor' | 'isAutoUpdate' | 'targetDomainId'> = {
      forVisitor: DomainVisitors.Any,
      targetDomainId: undefined,
      isAutoUpdate: true
    };

    const handleFinish = useCallback(
      (values: Pick<DomainRegluingsFormFields, 'forVisitor' | 'isAutoUpdate' | 'targetDomainId'>): void => {
        setFormErrors(null);
        createDomainRegluingMutation({ siteId, originDomainId: domainId, ...values });
      },
      []
    );

    const handleBack = useCallback((): void => {
      form.resetFields();
      handleToggleRegluingFormVisibility();
    }, []);

    const handleFinishFailed = useCallback((): void => {
      setFormErrors(null);
    }, []);

    return (
      <>
        <DomainRegluingsCreateFormView
          domainId={ domainId }
          siteId={ siteId }
          form={ form }
          formName={ 'domain-regluing-create' }
          formType={ 'create' }
          initialValues={ initialValues }
          isFormLoading={ createDomainRegluingIsLoading }
          validationSchema={ domainRegluingsFormValidationSchema }
          formErrors={ formErrors }
          handleBack={ handleBack }
          handleFinish={ handleFinish }
          handleFinishFailed={ handleFinishFailed }
        />
      </>
    );
  }
);
